import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ViewerCanvas } from "@/components/viewer/ViewerCanvas";
import { ViewerPermissionGuard } from "@/components/permissions/ViewerPermissionGuard";
import {
  useViewerStore,
  useViewerLoadingState,
  useViewerProject,
  useViewerPages,
  useViewerCurrentPageId,
  useViewerMultiPageMode,
} from "@/stores/viewer/viewer-store";
import { SensorControlConfig, DeviceBinding } from "@/components/shared/rendering/InteractionContext";
import { executeDeviceCommand } from "@/integrations/kasa/deviceCommandMapper";
import {
  type ControlEvent,
  executeHomeAssistantAction,
  getHomeAssistantPlaceholderAction,
  HomeAssistantReviewRequiredError,
  type PreparedHomeAssistantServiceCall,
} from "@/services/homeAssistant/executeAction";
import { useDeviceStateSync } from "@/hooks/useDeviceStateSync";
import { useNavigationStore } from "@/stores/navigation/navigation-store";
import { useHAStore } from "@/stores/integrations/ha-store";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ProjectSharingDialog } from "@/components/project/ProjectSharingDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { NavigationConfig } from "@/components/shared/rendering/InteractionContext";
import {
  getApiErrorMessage,
  getApiErrorStatus,
} from "@/services/api/client";
import {
  HAConfirmationDialog,
  type HAConfirmationDialogDetails,
  type HAConfirmationDialogPayloadEntry,
} from "@/components/integrations/HAConfirmationDialog";
import type { Component } from "@/types";

type ViewerComponentSnapshot = Pick<Component, "properties">;

interface PendingHAConfirmation {
  preparedCall: PreparedHomeAssistantServiceCall;
  details: HAConfirmationDialogDetails;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isDeviceBinding = (value: unknown): value is DeviceBinding => {
  if (!isRecord(value)) return false;
  if (value.type !== "kasa") return false;
  if (typeof value.deviceId !== "string" || typeof value.deviceName !== "string") {
    return false;
  }
  if (
    typeof value.controllerDeviceId !== "string" ||
    typeof value.orgSlug !== "string"
  ) {
    return false;
  }
  if (!isRecord(value.capabilities)) return false;

  return (
    typeof value.capabilities.toggle === "boolean" &&
    typeof value.capabilities.brightness === "boolean"
  );
};

const getComponentAction = (
  component: ViewerComponentSnapshot | null | undefined,
): Record<string, unknown> | null =>
  isRecord(component?.properties.action) ? component.properties.action : null;

const getComponentActionDeviceId = (
  component: ViewerComponentSnapshot | null | undefined,
): string | null => {
  const action = getComponentAction(component);
  return typeof action?.deviceId === "string" ? action.deviceId : null;
};

const getComponentDeviceBinding = (
  component: ViewerComponentSnapshot | null | undefined,
): DeviceBinding | null => {
  const action = getComponentAction(component);
  return isDeviceBinding(action?.deviceBinding) ? action.deviceBinding : null;
};

const buildHomeAssistantControlEvent = (
  config: SensorControlConfig,
): ControlEvent => {
  const parameters = { ...config.parameters };
  return {
    value: parameters.value,
    parameters,
  };
};

const MOMENTARY_COMMANDS_TO_IGNORE = new Set(["release", "up"]);

const SENSITIVE_PAYLOAD_KEY_PATTERN =
  /(authorization|token|secret|password|credential|cookie|api[_-]?key|header|url|uri|endpoint|webhook|host)/i;

const SENSITIVE_PAYLOAD_VALUE_PATTERN =
  /^(?:https?|wss?):\/\/|^bearer\s+/i;

const getControlCommand = (event: ControlEvent): string | null => {
  const command = event.parameters?.command;
  return typeof command === "string" ? command.toLowerCase() : null;
};

const shouldExecuteHomeAssistantEvent = (event: ControlEvent): boolean => {
  const command = getControlCommand(event);
  return command === null || !MOMENTARY_COMMANDS_TO_IGNORE.has(command);
};

const isSensitivePayloadKey = (key: string): boolean =>
  SENSITIVE_PAYLOAD_KEY_PATTERN.test(key);

const isSensitivePayloadValue = (value: string): boolean =>
  SENSITIVE_PAYLOAD_VALUE_PATTERN.test(value.trim());

const formatPayloadValue = (value: unknown): string | null => {
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (typeof value !== "string") {
    return null;
  }

  return isSensitivePayloadValue(value) ? null : value;
};

const summarizePreparedCallPayload = (
  data: PreparedHomeAssistantServiceCall["data"],
): {
  payloadEntries: HAConfirmationDialogPayloadEntry[];
  payloadNotice: string | null;
} => {
  if (!data) {
    return {
      payloadEntries: [],
      payloadNotice: null,
    };
  }

  const payloadEntries: HAConfirmationDialogPayloadEntry[] = [];
  let hiddenEntryCount = 0;

  for (const [key, rawValue] of Object.entries(data)) {
    if (isSensitivePayloadKey(key)) {
      hiddenEntryCount += 1;
      continue;
    }

    const formattedValue = formatPayloadValue(rawValue);
    if (formattedValue === null) {
      hiddenEntryCount += 1;
      continue;
    }

    payloadEntries.push({
      key,
      value: formattedValue,
    });
  }

  const payloadNotice =
    hiddenEntryCount === 0
      ? null
      : payloadEntries.length === 0
        ? "Additional payload values are hidden for safety."
        : "Some payload values are hidden for safety.";

  return {
    payloadEntries,
    payloadNotice,
  };
};

const buildPendingHAConfirmation = (
  error: HomeAssistantReviewRequiredError,
): PendingHAConfirmation => {
  const { payloadEntries, payloadNotice } = summarizePreparedCallPayload(
    error.request.preparedCall.data,
  );

  return {
    preparedCall: error.request.preparedCall,
    details: {
      entityId: error.request.preparedCall.entityId,
      domain: error.request.preparedCall.domain,
      service: error.request.preparedCall.service,
      serviceLabel: error.action.serviceLabel,
      safetyNote: error.action.safetyNote,
      payloadEntries,
      payloadNotice,
    },
  };
};

const getHomeAssistantErrorDescription = (error: unknown): string => {
  const status = getApiErrorStatus(error);
  if (status === 401 || status === 403) {
    return "Your current session is not authorized to run this Home Assistant action.";
  }

  return getApiErrorMessage(error, "Unknown error");
};

interface ViewerPageProps {
  showSharingDialog?: boolean;
  setShowSharingDialog?: (show: boolean) => void;
}

export const ViewerPage: React.FC<ViewerPageProps> = ({
  showSharingDialog = false,
  setShowSharingDialog,
}) => {
  const { projectId, pageId, orgSlug } = useParams<{
    projectId: string;
    pageId?: string;
    orgSlug?: string;
  }>();
  const navigate = useNavigate();
  const { isLoading, error } = useViewerLoadingState();
  const project = useViewerProject();
  const pages = useViewerPages();
  const currentPageId = useViewerCurrentPageId();
  const isMultiPage = useViewerMultiPageMode();
  const { loadProject, clearError, switchToPage, clearProject, setAllowInteraction, isFullscreen } = useViewerStore();
  const { navigateToProject } = useNavigationStore();
  const { toast } = useToast();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [pendingHAConfirmation, setPendingHAConfirmation] =
    useState<PendingHAConfirmation | null>(null);
  const [isConfirmingHAAction, setIsConfirmingHAAction] = useState(false);

  // Enable interactions for functional components (buttons, inputs, etc.)
  useEffect(() => {
    setAllowInteraction(true);
  }, [setAllowInteraction]);

  // Sync device state into bound components (polls every 5 seconds)
  useDeviceStateSync();

  const handleCancelHAConfirmation = useCallback(() => {
    if (isConfirmingHAAction) {
      return;
    }

    setPendingHAConfirmation(null);
  }, [isConfirmingHAAction]);

  const handleConfirmHAConfirmation = useCallback(async () => {
    if (!pendingHAConfirmation) {
      return;
    }

    setIsConfirmingHAAction(true);

    try {
      await useHAStore.getState().callService(
        pendingHAConfirmation.preparedCall.instanceId,
        pendingHAConfirmation.preparedCall.domain,
        pendingHAConfirmation.preparedCall.service,
        pendingHAConfirmation.preparedCall.entityId,
        pendingHAConfirmation.preparedCall.data,
      );

      setPendingHAConfirmation(null);
    } catch (error) {
      toast({
        title: "Home Assistant command failed",
        description: getHomeAssistantErrorDescription(error),
        variant: "destructive",
      });
    } finally {
      setIsConfirmingHAAction(false);
    }
  }, [pendingHAConfirmation, toast]);

  // Handle sensor control events (e.g., RadialDial value changes, device control)
  const handleSensorControl = useCallback(async (config: SensorControlConfig) => {
    // Find the component that triggered this — search by component ID first, then by action.deviceId
    const store = useViewerStore.getState();
    let component = store.getComponentById(config.sensorId);
    if (!component) {
      component = store.components.find((candidate) => {
        return getComponentActionDeviceId(candidate) === config.sensorId;
      });
    }

    // Check for device binding — route to physical device if bound
    const deviceBinding = getComponentDeviceBinding(component);

    if (deviceBinding) {
      return executeDeviceCommand(config, deviceBinding);
    }

    // Home Assistant placeholder action → relay through the cloud bridge.
    const haAction = getHomeAssistantPlaceholderAction(component);
    if (haAction && component) {
      const controlEvent = buildHomeAssistantControlEvent(config);
      if (!shouldExecuteHomeAssistantEvent(controlEvent)) {
        return {
          success: true,
          data: {
            entityId: haAction.entityId,
            service: haAction.service,
            skipped: "momentary-release",
          },
          timestamp: Date.now(),
        };
      }

      try {
        await executeHomeAssistantAction(component, controlEvent);
        return { success: true, data: { entityId: haAction.entityId, service: haAction.service }, timestamp: Date.now() };
      } catch (err) {
        if (err instanceof HomeAssistantReviewRequiredError) {
          setPendingHAConfirmation(buildPendingHAConfirmation(err));
        } else {
          toast({
            title: "Home Assistant command failed",
            description: getHomeAssistantErrorDescription(err),
            variant: "destructive",
          });
        }
        return {
          success: false,
          error:
            err instanceof HomeAssistantReviewRequiredError
              ? "Confirmation required"
              : getHomeAssistantErrorDescription(err),
          timestamp: Date.now(),
        };
      }
    }

    // Fallback: update local component state (existing behavior for unbound components)
    if (config.parameters?.command === 'set' && config.parameters?.value != null) {
      const existing = store.getComponentById(config.sensorId);
      if (existing) {
        const prop =
          typeof config.parameters.property === "string"
            ? config.parameters.property
            : "value";
        store.updateComponent(config.sensorId, {
          properties: { ...existing.properties, [prop]: config.parameters.value },
        });
      }
    }
    return { success: true, data: config.parameters, timestamp: Date.now() };
  }, [toast]);

  // Set org slug from URL params for file storage and other org-scoped features
  useEffect(() => {
    useViewerStore.getState().setViewerOrgSlug(orgSlug || null);
  }, [orgSlug]);

  // Debug content container in fullscreen
  useEffect(() => {
    if (contentRef.current && isFullscreen) {
      setTimeout(() => {
        const content = contentRef.current;
        if (content) {
          console.log('📦 [VIEWER_PAGE] Content container in fullscreen:', {
            scrollHeight: content.scrollHeight,
            clientHeight: content.clientHeight,
            canScroll: content.scrollHeight > content.clientHeight,
            overflow: window.getComputedStyle(content).overflow,
            overflowY: window.getComputedStyle(content).overflowY,
            height: window.getComputedStyle(content).height,
            minHeight: window.getComputedStyle(content).minHeight,
            classes: content.className,
          });
        }
      }, 100);
    }
  }, [isFullscreen]);

  // Navigation handler for button clicks (same-project navigation)
  const handleNavigate = async (config: {
    targetPageId?: string;
    parameters?: Record<string, unknown>;
  }) => {
    if (!config.targetPageId) {
      console.error("❌ [VIEWER_PAGE] No target page ID provided");
      return false;
    }

    // Check if target page exists in current project (support both ID and name lookup)
    const targetPage = pages.find(
      (p) => p.id === config.targetPageId || p.name === config.targetPageId
    );

    if (!targetPage) {
      console.error("❌ [VIEWER_PAGE] Target page not found:", config.targetPageId);
      return false;
    }

    try {
      // Update URL to reflect new page (always use actual page ID, not the target value)
      navigate(`/viewer/${projectId}/page/${targetPage.id}`, { replace: false });

      // Update viewer store to show the new page
      switchToPage(targetPage.id);

      return true;
    } catch (error) {
      console.error("❌ [VIEWER_PAGE] Navigation failed:", error);
      return false;
    }
  };

  // Cross-project navigation handler
  const handleNavigateProject = async (config: NavigationConfig): Promise<boolean> => {
    if (!config.targetProjectId) {
      console.error("❌ [VIEWER_PAGE] No target project ID provided");
      return false;
    }

    const result = await navigateToProject({
      projectId: config.targetProjectId,
      pageId: config.targetPageId,
      openInNewTab: config.openInNewTab,
    });

    if (!result.success && result.error) {
      // Show error toast based on error type
      if (result.error.type === "access_denied") {
        toast({
          title: "Access Denied",
          description: result.error.reason || "You do not have permission to access this project.",
          variant: "destructive",
        });
      } else if (result.error.type === "not_found") {
        toast({
          title: "Project Not Found",
          description: "The target project could not be found.",
          variant: "destructive",
        });
      } else if (result.error.type === "network_error") {
        toast({
          title: "Navigation Blocked",
          description: result.error.message || "Unable to open the project. Please check your popup blocker settings.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Navigation Failed",
          description: "Unable to navigate to the target project.",
          variant: "destructive",
        });
      }
    }

    return result.success;
  };

  // Load project on mount and force reload when coming from editor
  useEffect(() => {
    if (!projectId) {
      navigate("/dashboard");
      return () => {
        clearError();
        clearProject();
      };
    }

    // Always clear project cache and reload to ensure fresh data.
    clearProject();
    void loadProject(projectId, pageId);

    return () => {
      clearError();
      clearProject(); // Clear viewer org override when leaving
    };
  }, [clearError, clearProject, loadProject, navigate, pageId, projectId]);

  // Handle page switching when pageId changes in URL
  useEffect(() => {
    if (project && isMultiPage && pageId && currentPageId !== pageId) {
      const targetPage = pages.find((p) => p.id === pageId);
      if (targetPage) {
        switchToPage(pageId);
      } else if (pages.length > 0) {
        // Page not found, redirect to default page
        const defaultPage = pages.find((p) => p.isDefault) || pages[0];
        navigate(`/viewer/${projectId}/page/${defaultPage.id}`, {
          replace: true,
        });
      }
    }
  }, [
    project,
    isMultiPage,
    pageId,
    currentPageId,
    pages,
    switchToPage,
    navigate,
    projectId,
  ]);

  // Handle multi-page projects without pageId in URL
  useEffect(() => {
    if (project && isMultiPage && !pageId && pages.length > 0) {
      const defaultPage = pages.find((p) => p.isDefault) || pages[0];
      navigate(`/viewer/${projectId}/page/${defaultPage.id}`, {
        replace: true,
      });
    }
  }, [project, isMultiPage, pageId, pages, navigate, projectId]);

  const handleCloseSharing = () => {
    setShowSharingDialog?.(false);
  };

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="rounded-full bg-destructive/10 p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Failed to Load Project</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => projectId && loadProject(projectId)}>
              Retry
            </Button>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading && !project) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <div className="text-muted-foreground">Loading project...</div>
        </div>
      </div>
    );
  }

  if (!projectId) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-destructive">
          <div className="text-lg font-medium mb-2">Invalid Project</div>
          <div className="text-sm">No project ID provided</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Main viewer content - min-h-0 allows nested overflow scrolling in fullscreen */}
      <div ref={contentRef} className="flex-1 flex flex-col relative min-h-0">
        <div className="flex-1 relative min-h-0">
          <ViewerPermissionGuard
            project={project}
            onAccessDenied={() => navigate("/dashboard")}
          >
            <ViewerCanvas
              projectId={projectId}
              pageId={pageId}
              enableInteractions={true}
              onNavigate={handleNavigate}
              onNavigateProject={handleNavigateProject}
              onSensorControl={handleSensorControl}
            />
          </ViewerPermissionGuard>
        </div>
      </div>

      {/* Sharing Dialog */}
      {project && (
        <Dialog open={showSharingDialog} onOpenChange={setShowSharingDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Share Project - {project.name}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <Alert className="mb-4">
                <Eye className="w-4 h-4" />
                <AlertDescription>
                  You're sharing a read-only view of this project. Recipients
                  will be able to view and interact with components but cannot
                  edit the design.
                </AlertDescription>
              </Alert>
              <ProjectSharingDialog
                project={project}
                isOpen={showSharingDialog}
                onClose={handleCloseSharing}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      <HAConfirmationDialog
        open={pendingHAConfirmation !== null}
        details={pendingHAConfirmation?.details ?? null}
        isExecuting={isConfirmingHAAction}
        onCancel={handleCancelHAConfirmation}
        onConfirm={handleConfirmHAConfirmation}
      />
    </>
  );
};

// Wrapper component for integration with existing routing patterns
export const ViewerPageWrapper: React.FC = () => {
  const [showSharingDialog, setShowSharingDialog] = React.useState(false);

  return (
    <ViewerPage
      showSharingDialog={showSharingDialog}
      setShowSharingDialog={setShowSharingDialog}
    />
  );
};
